// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import { IconMemo } from "@insite/mobius/Icon";
import ChevronDown from "@insite/mobius/Icons/ChevronDown";
import ChevronUp from "@insite/mobius/Icons/ChevronUp";
import getColor from "@insite/mobius/utilities/getColor";
import throttle from "lodash/throttle";
import React, { useCallback, useEffect } from "react";
import styled, { ThemeProps } from "styled-components";

const ScrollIndicator = styled.span<{ direction?: "up" | "down" } & ThemeProps<BaseTheme>>`
    position: absolute;
    width: 100%;
    height: 20px;
    background: linear-gradient(0deg, transparent, white);
    z-index: 1;
    color: ${getColor("primary.contrast")};
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    transition: all ease-in-out 250ms;
    pointer-events: none;
    opacity: 0;

    &.up {
        top: 0;
        &.showUp {
            opacity: 1;
            //pointer-events: auto;
            //cursor: pointer;
        }
        &.onTop {
            // top reached style
        }
    }

    &.down {
        background: linear-gradient(180deg, transparent, white);
        bottom: 0;
        &.showDown {
            opacity: 1;
            //pointer-events: auto;
            //cursor: pointer;
        }
        &.onBottom {
            // top reached style
        }
    }
`;

export const FancyScroll = ({ parentRef }: { parentRef: React.RefObject<HTMLElement | null> }) => {
    const top = React.useRef<HTMLSpanElement>(null);
    const bottom = React.useRef<HTMLSpanElement>(null);

    const injectClass = useCallback(() => {
        if (parentRef && parentRef.current) {
            const scrollSize = Math.ceil(parentRef.current.scrollHeight - parentRef.current.clientHeight);
            const scrollTop = Math.ceil(parentRef.current?.scrollTop);
            const reachBottom = scrollTop >= scrollSize;

            const onTop = !!(scrollTop <= 0 && scrollSize);
            const onBottom = !!(reachBottom && scrollSize && scrollTop);
            const showUp = scrollTop !== 0;
            const showDown = !reachBottom;

            // reach events
            top.current?.classList?.toggle("onTop", onTop);
            bottom.current?.classList?.toggle("onBottom", onBottom);

            // appearance events
            top.current?.classList?.toggle("showUp", showUp);
            bottom.current?.classList?.toggle("showDown", showDown);
        }
    }, [parentRef, top, bottom]);

    const injectClassThrottle = throttle(injectClass, 100);

    useEffect(() => {
        if (parentRef && parentRef.current) {
            setTimeout(() => injectClassThrottle(), 0);
            parentRef.current.addEventListener("scroll", injectClassThrottle);
        }
        return () => {
            if (parentRef && parentRef.current) {
                parentRef.current.removeEventListener("scroll", injectClassThrottle);
            }
        };
    }, [parentRef]);

    useEffect(() => {
        const stepSize = 2;
        const framePeriod = 15;
        let intervalIdForScrollingTop: NodeJS.Timeout | undefined;
        let intervalIdForScrollingBottom: NodeJS.Timeout | undefined;

        const cancelScrollingTop = () => {
            clearInterval(intervalIdForScrollingTop);
        };

        const startScrollingTop = () => {
            const element = parentRef.current!;
            const scrollTo = (iterator: number) => {
                const newScrollingPosition: number = element.scrollTop - (stepSize + iterator * 0.2);
                if (newScrollingPosition > 0) {
                    element.scrollTo({
                        top: newScrollingPosition,
                    });
                    return false;
                }
                element.scrollTo({
                    top: 0,
                });
                return true;
            };
            let i = 0;
            if (!scrollTo(i++)) {
                intervalIdForScrollingTop = setInterval(() => {
                    if (scrollTo(i++)) {
                        cancelScrollingTop();
                    }
                }, framePeriod);
            }
        };

        const cancelScrollingBottom = () => {
            clearInterval(intervalIdForScrollingBottom);
        };

        const startScrollingBottom = () => {
            const element = parentRef.current!;
            const scrollTo = (iterator: number) => {
                const newScrollingPosition: number = element.scrollTop + (stepSize + iterator * 0.2);
                const maxYPosition = element.scrollHeight - element.clientHeight;
                if (newScrollingPosition < maxYPosition) {
                    element.scrollTo({
                        top: newScrollingPosition,
                    });
                    return false;
                }
                element.scrollTo({
                    top: maxYPosition,
                });
                return true;
            };
            let i = 0;
            if (!scrollTo(i++)) {
                intervalIdForScrollingBottom = setInterval(() => {
                    if (scrollTo(i++)) {
                        cancelScrollingTop();
                    }
                }, framePeriod);
            }
        };

        if (parentRef && parentRef.current) {
            if (top && top.current) {
                top.current?.addEventListener("mouseenter", startScrollingTop);
                top.current?.addEventListener("mouseleave", cancelScrollingTop);
            }
            if (bottom && bottom.current) {
                bottom.current?.addEventListener("mouseenter", startScrollingBottom);
                bottom.current?.addEventListener("mouseleave", cancelScrollingBottom);
            }
        }

        return () => {
            if (parentRef && parentRef.current) {
                if (top && top.current) {
                    top.current?.removeEventListener("mouseenter", startScrollingTop);
                    top.current?.removeEventListener("mouseleave", cancelScrollingTop);
                }
                if (bottom && bottom.current) {
                    bottom.current?.removeEventListener("mouseenter", startScrollingBottom);
                    bottom.current?.removeEventListener("mouseleave", cancelScrollingBottom);

                }
            }
        };
    }, [parentRef, top, bottom]);

    return (
        <>
            <ScrollIndicator className="up" ref={top}>
                <IconMemo src={ChevronUp} size={14} />
            </ScrollIndicator>
            <ScrollIndicator className="down" ref={bottom}>
                <IconMemo src={ChevronDown} size={14} />
            </ScrollIndicator>
        </>
    );
};

export default FancyScroll;
